import React from 'react';
import { Skill, FormErrors } from '../types';

interface BasicInfoSectionProps {
  formData: Skill;
  setFormData: (data: Skill) => void;
  errors: FormErrors;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  formData,
  setFormData,
  errors,
}) => {
  const handleChange = (field: keyof Skill, value: string | number | boolean | null) => {
    setFormData({ ...formData, [field]: value });
  };

  const skillKinds = [
    ['言語', 'language'],
    ['フレームワーク', 'framework'],
    ['ライブラリ', 'library'],
    ['ランタイム', 'runtime'],
    ['OS', 'os'],
    ['クラウド', 'cloud'],
    ['データベース', 'db'],
    ['ツール', 'tool'],
    ['CI/CD', 'ci_cd'],
    ['IaC', 'iac'],
    ['分析', 'analytics'],
    ['デザインツール', 'design_tool'],
    ['その他', 'generic']
  ];

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="name" className="form-label">
          スキル名 <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
          id="name"
          value={formData.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />
        {errors.name && <div className="invalid-feedback">{errors.name.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="kind" className="form-label">
          スキル分類
        </label>
        <select
          className={`form-select ${errors.kind ? 'is-invalid' : ''}`}
          id="kind"
          value={formData.kind || 'generic'}
          onChange={(e) => handleChange('kind', e.target.value)}
        >
          {skillKinds.map(([label, value]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {errors.kind && <div className="invalid-feedback">{errors.kind.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="parent_id" className="form-label">
          親スキル
        </label>
        <input
          type="number"
          className={`form-control ${errors.parent_id ? 'is-invalid' : ''}`}
          id="parent_id"
          value={formData.parent_id || ''}
          onChange={(e) => handleChange('parent_id', e.target.value ? parseInt(e.target.value, 10) : null)}
          placeholder="親スキルID（任意）"
        />
        <small className="form-text text-muted">検索して選択できます</small>
        {errors.parent_id && <div className="invalid-feedback">{errors.parent_id.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="signature_rank" className="form-label">
          署名順序
        </label>
        <input
          type="number"
          className={`form-control ${errors.signature_rank ? 'is-invalid' : ''}`}
          id="signature_rank"
          min="1"
          value={formData.signature_rank || 1000}
          onChange={(e) => handleChange('signature_rank', parseInt(e.target.value, 10) || 1000)}
        />
        <small className="form-text text-muted">クロスLP生成時の署名順序（数値が小さいほど優先）</small>
        {errors.signature_rank && <div className="invalid-feedback">{errors.signature_rank.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="slug" className="form-label">
          スラッグ
        </label>
        <input
          type="text"
          className={`form-control ${errors.slug ? 'is-invalid' : ''}`}
          id="slug"
          value={formData.slug || ''}
          onChange={(e) => handleChange('slug', e.target.value)}
          placeholder="自動生成されます"
        />
        <small className="form-text text-muted">URL用のスラッグ（空の場合は自動生成）</small>
        {errors.slug && <div className="invalid-feedback">{errors.slug.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="synonyms" className="form-label">
          シノニム（改行区切り）
        </label>
        <textarea
          className={`form-control ${errors.synonyms ? 'is-invalid' : ''}`}
          id="synonyms"
          rows={3}
          value={formData.synonyms || ''}
          onChange={(e) => handleChange('synonyms', e.target.value)}
          placeholder="AWS&#10;Amazon Web Service&#10;AmazonWebService"
        />
        <small className="form-text text-muted">類義語を改行区切りで入力してください</small>
        {errors.synonyms && <div className="invalid-feedback">{errors.synonyms.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="published"
            checked={formData.published || false}
            onChange={(e) => handleChange('published', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="published">
            公開ステータス
          </label>
        </div>
      </div>

      <hr className="my-4" />

      <h4 className="mb-3">コンテンツ情報</h4>

      <div className="mb-3">
        <label htmlFor="job_description" className="form-label">
          仕事内容の説明
        </label>
        <textarea
          className={`form-control ${errors.job_description ? 'is-invalid' : ''}`}
          id="job_description"
          rows={8}
          value={formData.job_description || ''}
          onChange={(e) => handleChange('job_description', e.target.value)}
        />
        {errors.job_description && <div className="invalid-feedback">{errors.job_description.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="required_skills" className="form-label">
          求められるスキル
        </label>
        <textarea
          className={`form-control ${errors.required_skills ? 'is-invalid' : ''}`}
          id="required_skills"
          rows={8}
          value={formData.required_skills || ''}
          onChange={(e) => handleChange('required_skills', e.target.value)}
        />
        {errors.required_skills && <div className="invalid-feedback">{errors.required_skills.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="study_methods" className="form-label">
          スキルを高めるための勉強方法
        </label>
        <textarea
          className={`form-control ${errors.study_methods ? 'is-invalid' : ''}`}
          id="study_methods"
          rows={8}
          value={formData.study_methods || ''}
          onChange={(e) => handleChange('study_methods', e.target.value)}
        />
        {errors.study_methods && <div className="invalid-feedback">{errors.study_methods.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="required_qualifications" className="form-label">
          必要な資格
        </label>
        <textarea
          className={`form-control ${errors.required_qualifications ? 'is-invalid' : ''}`}
          id="required_qualifications"
          rows={8}
          value={formData.required_qualifications || ''}
          onChange={(e) => handleChange('required_qualifications', e.target.value)}
        />
        {errors.required_qualifications && <div className="invalid-feedback">{errors.required_qualifications.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="open_to_beginners" className="form-label">
          未経験からでも応募できるか
        </label>
        <textarea
          className={`form-control ${errors.open_to_beginners ? 'is-invalid' : ''}`}
          id="open_to_beginners"
          rows={8}
          value={formData.open_to_beginners || ''}
          onChange={(e) => handleChange('open_to_beginners', e.target.value)}
        />
        {errors.open_to_beginners && <div className="invalid-feedback">{errors.open_to_beginners.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="career_prospects" className="form-label">
          将来性やキャリア
        </label>
        <textarea
          className={`form-control ${errors.career_prospects ? 'is-invalid' : ''}`}
          id="career_prospects"
          rows={8}
          value={formData.career_prospects || ''}
          onChange={(e) => handleChange('career_prospects', e.target.value)}
        />
        {errors.career_prospects && <div className="invalid-feedback">{errors.career_prospects.join(', ')}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="faq" className="form-label">
          よくある質問
        </label>
        <textarea
          className={`form-control ${errors.faq ? 'is-invalid' : ''}`}
          id="faq"
          rows={8}
          value={formData.faq || ''}
          onChange={(e) => handleChange('faq', e.target.value)}
        />
        {errors.faq && <div className="invalid-feedback">{errors.faq.join(', ')}</div>}
      </div>
    </div>
  );
};

export default BasicInfoSection;

